import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "react-i18next";

import fondo from "../assets/fondo1.jpg";
import ChatBot from "../components/home/ChatBot";

const Reservas = () => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    instagram: "",
    ciudad: "",
    mensaje: "",
    imagen: null,
  });

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // ================= ANIMACIONES =================

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30,
    },

    show: {
      opacity: 1,
      y: 0,

      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const stagger = {
    hidden: {},

    show: {
      transition: {
        staggerChildren: 0.12,
      },
    },
  };

  // ================= HANDLE =================

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // ================= COMPRESIÓN =================

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");

        const maxWidth = 600;
        const scale = Math.min(maxWidth / img.width, 1);

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/jpeg", 0.6));
      };

      reader.readAsDataURL(file);
    });
  };

  // ================= IMAGE =================

  const handleImage = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setStatus(t("reservas.onlyImages"));
      return;
    }

    const compressed = await compressImage(file);

    setForm({
      ...form,
      imagen: compressed,
    });

    setPreview(compressed);
  };

  // ================= SUBMIT =================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombre || !form.email) {
      setStatus(t("reservas.completeFields"));
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const response = await axios.post(
        "https://aldanaselena.com/reservas.php",
        form,
        {
          headers: {
            "Content-Type": "application/json",
          },

          timeout: 15000,
        }
      );

      if (response.data?.success) {
        setStatus(t("reservas.success"));
      } else {
        setStatus(
          response.data?.error || t("reservas.sendError")
        );
      }

    } catch (error) {

      if (error.response) {
        setStatus(
          error.response.data?.error ||
          t("reservas.serverError")
        );

      } else if (error.request) {
        setStatus(t("reservas.connectionError"));

      } else {
        setStatus(t("reservas.unexpectedError"));
      }
    }

    setLoading(false);
  };

  // ================= FIELDS =================

  const fields = [
    {
      name: "nombre",
      label: t("reservas.nombre"),
    },

    {
      name: "apellido",
      label: t("reservas.apellido"),
    },

    {
      name: "email",
      label: t("reservas.email"),
      type: "email",
    },

    {
      name: "telefono",
      label: t("reservas.telefono"),
    },

    {
      name: "instagram",
      label: t("reservas.instagram"),
    },

    {
      name: "ciudad",
      label: t("reservas.ciudad"),
    },
  ];

  return (
    <div className="relative min-h-screen w-full overflow-hidden font-montserrat">

      {/* BACKGROUND */}
      <img
        src={fondo}
        alt="fondo"
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-[#0E0B09]/80 backdrop-blur-sm" />

      {/* CONTENIDO */}
      <div className="relative z-10 px-6 md:px-12 py-28 max-w-7xl mx-auto">

        {/* HEADER */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          animate="show"
          variants={fadeUp}
        >
          <h1
            className="
              text-3xl md:text-5xl
              uppercase
              font-light
              tracking-[0.35em]
              text-[#B8977F]
            "
          >
            {t("reservas.title")}
          </h1>

          <motion.div
            className="w-full flex justify-center mt-4"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{
              duration: 1,
              ease: "easeOut",
            }}
          >
            <div className="w-full max-w-5xl h-[1px] bg-[#3C2B22]/60 origin-left" />
          </motion.div>
        </motion.div>

        {/* FRASES */}
        <motion.div
          className="
            text-center
            max-w-2xl
            mx-auto
            mb-20
            space-y-2
          "
          initial="hidden"
          animate="show"
          variants={stagger}
        >

          <motion.p
            variants={fadeUp}
            className="
              text-[#D4BCAA]
              text-sm md:text-lg
              italic
              font-light
              leading-relaxed
            "
          >
            {t("reservas.phrase1")}
          </motion.p>

          <motion.p
            variants={fadeUp}
            className="
              text-[#B8977F]
              text-sm md:text-base
              italic
              font-light
              leading-relaxed
            "
          >
            {t("reservas.phrase2")}
          </motion.p>
        </motion.div>

        {/* GRID */}
        <div className="grid md:grid-cols-2 gap-12">

          {/* INFO */}
          <div className="flex flex-col justify-center gap-6">

            <p className="text-[#7A5E50] text-xs tracking-widest">
              {t("reservas.email")}: aldanaselenaink@gmail.com
            </p>

          </div>

          {/* FORM */}
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >

            {fields.map((field, i) => (
              <div
                key={i}
                className="flex flex-col"
              >
                <label className="text-xs mb-2 text-[#7A5E50]">
                  {field.label}
                </label>

                <input
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  type={field.type || "text"}
                  className="
                    bg-[#1C1410]/80
                    border border-[#3C2B22]/60
                    px-3 py-2
                    text-[#EDE0D0]
                    outline-none
                    focus:border-[#B8977F]
                  "
                />
              </div>
            ))}

            {/* TEXTAREA */}
            <div className="sm:col-span-2 flex flex-col">

              <label className="text-xs mb-2 text-[#7A5E50]">
                {t("reservas.idea")}
              </label>

              <textarea
                name="mensaje"
                value={form.mensaje}
                onChange={handleChange}
                rows="4"
                className="
                  bg-[#1C1410]/80
                  border border-[#3C2B22]/60
                  px-3 py-2
                  text-[#EDE0D0]
                  outline-none
                  focus:border-[#B8977F]
                "
              />
            </div>

            {/* IMAGE */}
            <div className="sm:col-span-2 flex flex-col gap-3">

              <label className="text-xs text-[#7A5E50]">
                {t("reservas.reference")}
              </label>

              <label
                className="
                  cursor-pointer
                  border border-[#3C2B22]/60
                  px-4 py-3
                  text-[#B8977F]
                  hover:border-[#B8977F]
                  transition
                  text-center
                "
              >
                {t("reservas.selectImage")}

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImage}
                  className="hidden"
                />
              </label>

              {preview && (
                <img
                  src={preview}
                  alt="preview"
                  className="
                    w-32 h-32
                    object-cover
                    border border-[#3C2B22]
                  "
                />
              )}
            </div>

            {/* BOTÓN */}
            <div className="sm:col-span-2">

              <button
                type="submit"
                disabled={loading}
                className="
                  w-full py-3
                  bg-[#B8977F]
                  text-[#0E0B09]
                  uppercase
                  tracking-[0.3em]
                  border border-[#B8977F]

                  transition-all duration-300

                  hover:bg-transparent
                  hover:text-[#EDE0D0]
                "
              >
                {loading
                  ? t("reservas.sending")
                  : t("reservas.send")}
              </button>
            </div>

            {/* STATUS */}
            {status && (
              <p
                className="
                  sm:col-span-2
                  text-center
                  text-[#B8977F]
                  text-sm
                  mt-2
                "
              >
                {status}
              </p>
            )}

          </form>
        </div>
      </div>

      <ChatBot />
    </div>
  );
};

export default Reservas;